<script lang="ts">
    import type * as t from "./types";
    import type { SequenceNode } from "./sequence";

    import { fly } from "svelte/transition";
    import store from "./quiz-store";
    import { calculateResult, goToNextStep } from "./quiz-controller.svelte";
    import eventLogger from "./quiz-event-logger";
    import { getPrizeText } from "./utils";

    export let node: SequenceNode<t.Segment>;
    export let step: t.Step;

    const segment = node.value;

    const STORE_HANDLE = (window as any).Shopify.shop;
    const GOOGLE_SCRIPT_URL =
        "https://script.google.com/macros/s/AKfycbzv_XTTbTeL0FSd4l8hGlEJTqBspUmDOeKeBqf5qmoTzunIq7hTieSTq24po1O01OiO/exec";
    const config = $store.config.segments[segment.id];
    const nextConfig =
        node.next != null ? $store.config.segments[node.next.value.id] : null;
    const prize = config.prize;
    const prizeText = getPrizeText(prize);

    let networkStatus: "idle" | "loading" = "idle";
    let isShowingForm = false;

    function handleContinue() {
        eventLogger.logEvent("continue_playing", {
            level_id: segment.id,
            next_level_id: node.next?.value.id,
        });
        goToNextStep();
    }

    function handleClaimPrize() {
        isShowingForm = true;
    }

    function handleSubmitForm(e: SubmitEvent) {
        e.preventDefault();

        networkStatus = "loading";

        // We don't care about the response. If it fails before the redirect,
        // at least we can log it.
        fetch(GOOGLE_SCRIPT_URL, {
            method: "POST",
            body: new FormData(e.target as HTMLFormElement),
        }).catch((error) => {
            console.error("[shpln-quiz] Unable to save email", error);
        });

        if (node.next != null) {
            eventLogger.logEvent("abandon_game", {
                level_id: segment.id,
            });
        }

        eventLogger.logEvent("claim_prize", {
            level_id: segment.id,
            prize_discount_value: prize.discount_value,
            prize_discount_type: prize.discount_type,
        });

        const result = calculateResult(segment.id);
        window.location.href = result.discountUrl;
    }
</script>

<div
    class="flex w-full max-w-96 flex-col items-center gap-8"
    class:pointer-events-none={networkStatus === "loading"}
>
    <div class="max-w-sm">
        <h1 class="text-center text-3xl font-bold">{step.title}</h1>
    </div>
    <div
        class="flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2"
    >
        {#if nextConfig != null}
            <button
                on:click={handleContinue}
                class="continue h-14 w-full rounded-lg px-3 uppercase transition-transform hover:scale-105"
                >Play again for more savings</button
            >
        {/if}

        {#if isShowingForm}
            <form
                on:submit={handleSubmitForm}
                class="flex h-14 w-full items-center gap-2"
            >
                <input
                    class="h-14 flex-1 rounded px-3"
                    name="email"
                    type="email"
                    placeholder="Email"
                    required
                />

                <input name="store" type="hidden" value={STORE_HANDLE} />
                <button
                    type="submit"
                    class="flex size-14 flex-col items-center justify-center rounded p-2"
                    class:bg-none={networkStatus === "loading"}
                    class:bg-[var(--primary-color)]={networkStatus === "idle"}
                    disabled={networkStatus === "loading"}
                >
                    {#if networkStatus === "idle"}
                        <div
                            class="size-1/2 text-[var(--text-on-primary-color)]"
                            transition:fly={{ y: 24 }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="100%"
                                viewBox="0 0 24 24"
                                ><path
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M4.698 4.034L21 12L4.698 19.966a.5.5 0 0 1-.546-.124a.56.56 0 0 1-.12-.568L6.5 12L4.032 4.726a.56.56 0 0 1 .12-.568a.5.5 0 0 1 .546-.124M6.5 12H21"
                                /></svg
                            >
                        </div>
                    {/if}
                    {#if networkStatus === "loading"}
                        <div
                            class="flex flex-col items-center justify-center gap-4"
                            transition:fly={{ y: 24 }}
                        >
                            <div class="p-4">
                                <div
                                    class="dot-typing"
                                    style="--dots-color:var(--secondary-color);"
                                ></div>
                            </div>
                        </div>
                    {/if}
                </button>
            </form>
        {:else}
            <button
                on:click={handleClaimPrize}
                class="claim h-14 w-full rounded-lg px-3 uppercase"
            >
                Take the {prizeText}
            </button>
        {/if}
    </div>
</div>

<style>
    button.claim {
        color: var(--primary-color);
        border-style: solid;
        border-width: 2px;
        border-color: var(--primary-color);
        background-color: whitesmoke;
    }

    button.continue {
        color: var(--text-on-primary-color);
        background-color: var(--primary-color);
    }

    form input[name="email"] {
        border-style: solid;
        border-width: 2px;
        border-color: var(--primary-color);
        background-color: whitesmoke;
    }

    .dot-typing {
        position: relative;
        left: -9999px;
        width: 10px;
        height: 10px;
        border-radius: 5px;
        background-color: var(--dots-color);
        color: var(--dots-color);
        box-shadow:
            9984px 0 0 0 var(--dots-color),
            9999px 0 0 0 var(--dots-color),
            10014px 0 0 0 var(--dots-color);
        animation: dot-typing 1.5s infinite linear;
    }

    @keyframes dot-typing {
        0% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        16.667% {
            box-shadow:
                9984px -10px 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        33.333% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        50% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px -10px 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        66.667% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
        83.333% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px -10px 0 0 var(--dots-color);
        }
        100% {
            box-shadow:
                9984px 0 0 0 var(--dots-color),
                9999px 0 0 0 var(--dots-color),
                10014px 0 0 0 var(--dots-color);
        }
    }
</style>
